import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import api from "../../utils/api";
import dayjs from "dayjs";
import MainCard from "components/MainCard";
import BusinessIcon from "@mui/icons-material/Business";
import PhoneIcon from "@mui/icons-material/Phone";
import LocationOnIcon from "@mui/icons-material/LocationOn";
import SettingsIcon from "@mui/icons-material/Settings";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import PaymentIcon from "@mui/icons-material/Payment";
import ImageIcon from "@mui/icons-material/Image";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import PersonIcon from "@mui/icons-material/Person";

import {
  Alert,
  Box,
  Grid,
  TextField,
  Button,
  Typography,
  MenuItem,
  Switch,
  FormControlLabel,
} from "@mui/material";
import useFormValidation from "hooks/useFormValidation";
import { validators } from "utils/formValidation";

import { LocalizationProvider, TimePicker } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";

export default function CreateStore() {
  const navigate = useNavigate();

  const [branchGroups, setBranchGroups] = useState([]);
  const [storeTypes, setStoreTypes] = useState([]);

  const [form, setForm] = useState({
    user_id: "",
    branch_group_id: "",
    store_type_id: "",
    title_en: "",
    title_ar: "",
    address_en: "",
    address_ar: "",
    primary_contact: "",
    secondary_contact: "",
    logo: null,
    cover_image: null,
    from_time: null,
    to_time: null,
    latitude: "",
    longitude: "",
    minimum_purchase: "",
    commission_rate: "",
    delivery_charge: "",
    delivery_range: "",
    cod_pay: false,
    online_pay: false,
    wallet_pay: false,
    card_pay: false,
    dining: false,
    take_away: false,
    delivery: false,
    busy: false,
    pure_veg: false,
    status: true,
    vendor_name: "",
    vendor_username: "",
    vendor_password: "",
    vendor_email: "",
    vendor_phone: "",
  });


  const [logoPreview, setLogoPreview] = useState(null);
  const [coverPreview, setCoverPreview] = useState(null);

  useEffect(() => { loadDropdowns(); }, []);

  const loadDropdowns = async () => {
    try {
      const branchRes = await api.get("/branch/group/list");
      const storeTypeRes = await api.get("/store/type/list");
      setBranchGroups(Array.isArray(branchRes.data?.data) ? branchRes.data.data : []);
      setStoreTypes(Array.isArray(storeTypeRes.data?.data) ? storeTypeRes.data.data : []);
    } catch (err) {
      console.error("Dropdown Load Error:", err);
      setBranchGroups([]);
      setStoreTypes([]);
    }
  };

  const { formError, validate, setServerError, clearFieldError, fieldErrorProps } = useFormValidation();

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    if (name) clearFieldError(name);
    setForm((prev) => ({
      ...prev,
      [name]:
        type === "checkbox" ? checked
          : type === "number" ? (value === "" ? "" : Number(value))
            : value,
    }));
  };

  const handleFileChange = (e) => {
    const { name, files } = e.target;
    if (!files?.[0]) return;
    const file = files[0];
    setForm((prev) => ({ ...prev, [name]: file }));
    if (name === "logo") setLogoPreview(URL.createObjectURL(file));
    if (name === "cover_image") setCoverPreview(URL.createObjectURL(file));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (
      !validate(
        {
          store_type_id: [validators.required("Store type")],
          title_en: [validators.required("Title (English)")],
          delivery_range: [validators.required("Delivery range")],
          vendor_name: [validators.required("Vendor name")],      // ← add
          vendor_username: [validators.required("Vendor username")],  // ← add
          vendor_password: [validators.required("Vendor password")],
        },
        form
      )
    ) {
      return;
    }
    try {
      const formData = new FormData();
      Object.keys(form).forEach((key) => {
        if (key === "from_time" && form.from_time) {
          formData.append(key, dayjs(form.from_time).format("HH:mm:ss"));
        } else if (key === "to_time" && form.to_time) {
          formData.append(key, dayjs(form.to_time).format("HH:mm:ss"));
        } else {
          formData.append(key, form[key] ?? "");
        }
      });
      await api.post("/store/add", formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      navigate("/store/list");
    } catch (error) {
      console.error(error);
      setServerError(error.response?.data?.message || "Create failed");
    }
  };

  const CardTitle = ({ icon, label }) => (
    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
      {icon}
      <Typography variant="h6">{label}</Typography>
    </Box>
  );

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      <Box>
        <Box sx={{ mb: 3, display: 'flex', alignItems: 'center', gap: 2 }}>
          <Button
            variant="outlined"
            color="secondary"
            startIcon={<ArrowBackIcon />}
            onClick={() => navigate(-1)}
            sx={{ borderRadius: 2 }}
          >
            Back
          </Button>
          <Box>
            <Typography variant="h4" fontWeight={700}>Create New Store</Typography>
            <Typography variant="body2" color="text.secondary">
              Fill in the details to register a new store in the system.
            </Typography>
          </Box>
        </Box>

        <form onSubmit={handleSubmit} noValidate>
          {formError ? (
            <Alert severity="error" sx={{ mb: 2 }}>
              {formError}
            </Alert>
          ) : null}
          <Grid container spacing={4}>

            {/* ── BASIC INFO ──────────────────────────────────────── */}
            <Grid size={12}>
              <MainCard
                contentSX={{ p: 3 }}
                title={<CardTitle icon={<BusinessIcon color="primary" />} label="Basic Information" />}
              >
                <Grid container spacing={3}>
                  <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                    <TextField
                      select fullWidth label="Branch Group"
                      name="branch_group_id"
                      value={form.branch_group_id}
                      onChange={handleChange}
                    >
                      <MenuItem value=""><em>Select Branch</em></MenuItem>
                      {branchGroups.map((bg) => (
                        <MenuItem key={bg.branch_group_id} value={bg.branch_group_id}>
                          {bg.franchise_name}
                        </MenuItem>
                      ))}
                    </TextField>
                  </Grid>

                  <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                    <TextField
                      select fullWidth label="Store Type *"
                      name="store_type_id"
                      value={form.store_type_id}
                      onChange={handleChange}
                      {...fieldErrorProps("store_type_id")}
                    >
                      <MenuItem value=""><em>Select Store Type</em></MenuItem>
                      {storeTypes.map((st) => (
                        <MenuItem key={st.store_type_id} value={st.store_type_id}>
                          {st.type_name}
                        </MenuItem>
                      ))}
                    </TextField>
                  </Grid>

                  <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                    <TextField
                      fullWidth required label="Title English "
                      name="title_en"
                      value={form.title_en}
                      onChange={handleChange}
                      {...fieldErrorProps("title_en")}
                    />
                  </Grid>

                  <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                    <TextField
                      fullWidth label="Title Arabic"
                      name="title_ar"
                      value={form.title_ar}
                      onChange={handleChange}
                    />
                  </Grid>
                </Grid>
              </MainCard>
            </Grid>

            {/* ── VENDOR ACCOUNT ──────────────────────────────────── */}
            <Grid size={12}>
              <MainCard
                contentSX={{ p: 3 }}
                title={<CardTitle icon={<PersonIcon color="primary" />} label="Vendor Account" />}
              >
                <Grid container spacing={3}>
                  <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                    <TextField
                      fullWidth required label="Vendor Name "
                      name="vendor_name"
                      value={form.vendor_name}
                      onChange={handleChange}
                      {...fieldErrorProps("vendor_name")}
                    />
                  </Grid>
                  <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                    <TextField
                      fullWidth required label="Username "
                      name="vendor_username"
                      value={form.vendor_username}
                      onChange={handleChange}
                      {...fieldErrorProps("vendor_username")}
                    />
                  </Grid>
                  <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                    <TextField
                      fullWidth required label="Password"
                      name="vendor_password"
                      type="password"
                      value={form.vendor_password}
                      onChange={handleChange}
                      {...fieldErrorProps("vendor_password")}
                    />
                  </Grid>
                  <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                    <TextField
                      fullWidth label="Vendor Email"
                      name="vendor_email"
                      type="email"
                      value={form.vendor_email}
                      onChange={handleChange}
                    />
                  </Grid>
                  <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                    <TextField
                      fullWidth label="Vendor Phone"
                      name="vendor_phone"
                      value={form.vendor_phone}
                      onChange={handleChange}
                    />
                  </Grid>
                </Grid>
              </MainCard>
            </Grid>

            {/* ── CONTACT ─────────────────────────────────────────── */}
            <Grid size={12}>
              <MainCard
                contentSX={{ p: 3 }}
                title={<CardTitle icon={<PhoneIcon color="primary" />} label="Contact Information" />}
              >
                <Grid container spacing={3}>
                  <Grid size={{ xs: 12, sm: 6 }}>
                    <TextField
                      fullWidth label="Primary Contact"
                      name="primary_contact"
                      value={form.primary_contact}
                      onChange={handleChange}
                    />
                  </Grid>
                  <Grid size={{ xs: 12, sm: 6 }}>
                    <TextField
                      fullWidth label="Secondary Contact"
                      name="secondary_contact"
                      value={form.secondary_contact}
                      onChange={handleChange}
                    />
                  </Grid>
                </Grid>
              </MainCard>
            </Grid>

            {/* ── LOCATION ────────────────────────────────────────── */}
            <Grid size={12}>
              <MainCard
                contentSX={{ p: 3 }}
                title={<CardTitle icon={<LocationOnIcon color="primary" />} label="Location Information" />}
              >
                <Grid container spacing={3}>
                  <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                    <TextField
                      type="number" fullWidth label="Latitude"
                      name="latitude" value={form.latitude}
                      onChange={handleChange}
                    />
                  </Grid>
                  <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                    <TextField
                      type="number" fullWidth label="Longitude"
                      name="longitude" value={form.longitude}
                      onChange={handleChange}
                    />
                  </Grid>
                  <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                    <TextField
                      fullWidth label="Address English"
                      name="address_en" value={form.address_en}
                      onChange={handleChange}
                    />
                  </Grid>
                  <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                    <TextField
                      fullWidth label="Address Arabic"
                      name="address_ar" value={form.address_ar}
                      onChange={handleChange}
                    />
                  </Grid>
                </Grid>
              </MainCard>
            </Grid>

            {/* ── TIMING ──────────────────────────────────────────── */}
            <Grid size={12}>
              <MainCard
                contentSX={{ p: 3 }}
                title={<CardTitle icon={<AccessTimeIcon color="primary" />} label="Store Timing" />}
              >
                <Grid container spacing={3}>
                  <Grid size={{ xs: 12, sm: 6 }}>
                    <TimePicker
                      label="Opening Time"
                      value={form.from_time}
                      onChange={(v) => setForm((prev) => ({ ...prev, from_time: v }))}
                      slotProps={{ textField: { fullWidth: true } }}
                    />
                  </Grid>
                  <Grid size={{ xs: 12, sm: 6 }}>
                    <TimePicker
                      label="Closing Time"
                      value={form.to_time}
                      onChange={(v) => setForm((prev) => ({ ...prev, to_time: v }))}
                      slotProps={{ textField: { fullWidth: true } }}
                    />
                  </Grid>
                </Grid>
              </MainCard>
            </Grid>

            {/* ── DELIVERY SETTINGS ───────────────────────────────── */}
            <Grid size={12}>
              <MainCard
                contentSX={{ p: 3 }}
                title={<CardTitle icon={<SettingsIcon color="primary" />} label="Delivery Settings" />}
              >
                <Grid container spacing={3}>
                  <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                    <TextField
                      type="number" fullWidth label="Minimum Purchase"
                      name="minimum_purchase" value={form.minimum_purchase}
                      onChange={handleChange}
                    />
                  </Grid>
                  <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                    <TextField
                      type="number" fullWidth label="Commission Rate"
                      name="commission_rate" value={form.commission_rate}
                      onChange={handleChange}
                    />
                  </Grid>
                  <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                    <TextField
                      type="number" fullWidth label="Delivery Charge"
                      name="delivery_charge" value={form.delivery_charge}
                      onChange={handleChange}
                    />
                  </Grid>
                  <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                    <TextField
                      type="number" fullWidth required label="Delivery Range (KM)"
                      name="delivery_range" value={form.delivery_range}
                      onChange={handleChange}
                      {...fieldErrorProps("delivery_range")}
                    />
                  </Grid>
                </Grid>
              </MainCard>
            </Grid>

            {/* ── PAYMENT METHODS ─────────────────────────────────── */}
            <Grid size={12}>
              <MainCard
                contentSX={{ p: 3 }}
                title={<CardTitle icon={<PaymentIcon color="primary" />} label="Payment Methods" />}
              >
                <Grid container spacing={2}>
                  {[
                    { label: "COD", name: "cod_pay" },
                    { label: "Online Pay", name: "online_pay" },
                    { label: "Wallet Pay", name: "wallet_pay" },
                    { label: "Card Pay", name: "card_pay" },
                  ].map((item) => (
                    <Grid size={{ xs: 12, sm: 6, md: 3 }} key={item.name}>
                      <FormControlLabel
                        control={
                          <Switch
                            name={item.name}
                            checked={form[item.name]}
                            onChange={handleChange}
                          />
                        }
                        label={item.label}
                      />
                    </Grid>
                  ))}
                </Grid>
              </MainCard>
            </Grid>

            {/* ── STORE FEATURES ──────────────────────────────────── */}
            <Grid size={12}>
              <MainCard
                contentSX={{ p: 3 }}
                title={<CardTitle icon={<BusinessIcon color="primary" />} label="Store Features" />}
              >
                <Grid container spacing={2}>
                  {[
                    { label: "Dining", name: "dining" },
                    { label: "Take Away", name: "take_away" },
                    { label: "Delivery", name: "delivery" },
                    { label: "Busy", name: "busy" },
                    { label: "Pure Veg", name: "pure_veg" },
                    { label: "Active Status", name: "status" },
                  ].map((item) => (
                    <Grid size={{ xs: 12, sm: 6, md: 4 }} key={item.name}>
                      <FormControlLabel
                        control={
                          <Switch
                            name={item.name}
                            checked={form[item.name]}
                            onChange={handleChange}
                          />
                        }
                        label={item.label}
                      />
                    </Grid>
                  ))}
                </Grid>
              </MainCard>
            </Grid>

            {/* ── STORE IMAGES ────────────────────────────────────── */}
            <Grid size={12}>
              <MainCard
                contentSX={{ p: 3 }}
                title={<CardTitle icon={<ImageIcon color="primary" />} label="Store Images" />}
              >
                <Grid container spacing={3}>
                  {/* Logo */}
                  <Grid size={{ xs: 12, sm: 6 }}>
                    <Button variant="outlined" component="label" fullWidth>
                      Upload Logo
                      <input
                        type="file" name="logo" hidden accept="image/*"
                        onChange={handleFileChange}
                      />
                    </Button>
                    {logoPreview && (
                      <Box mt={1.5}>
                        <img
                          src={logoPreview} alt="Logo Preview"
                          style={{ height: 80, borderRadius: 8, objectFit: "cover" }}
                        />
                      </Box>
                    )}
                  </Grid>

                  {/* Cover */}
                  <Grid size={{ xs: 12, sm: 6 }}>
                    <Button variant="outlined" component="label" fullWidth>
                      Upload Cover Image
                      <input
                        type="file" name="cover_image" hidden accept="image/*"
                        onChange={handleFileChange}
                      />
                    </Button>
                    {coverPreview && (
                      <Box mt={1.5}>
                        <img
                          src={coverPreview} alt="Cover Preview"
                          style={{ height: 120, width: "100%", borderRadius: 8, objectFit: "cover" }}
                        />
                      </Box>
                    )}
                  </Grid>
                </Grid>
              </MainCard>
            </Grid>

            {/* ── SUBMIT BAR ──────────────────────────────────────── */}
            <Grid size={12}>
              <Box
                sx={{
                  position: "sticky", bottom: 0,
                  bgcolor: "#fff", p: 2,
                  borderTop: "1px solid #eee",
                  display: "flex", justifyContent: "flex-end",
                  gap: 2,
                  zIndex: 10,
                }}
              >
                <Button variant="outlined" color="secondary" onClick={() => navigate(-1)}>
                  Cancel
                </Button>
                <Button type="submit" variant="contained" size="large">
                  Create Store
                </Button>
              </Box>
            </Grid>

          </Grid>
        </form>
      </Box>
    </LocalizationProvider>
  );
}